import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { GoogleLoginButton } from '@/components/GoogleLoginButton';
import { toast } from 'sonner';
import { GraduationCap, Flame, Rocket, ArrowLeft, Gift } from 'lucide-react';

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const prefillRef = (sp.get('ref') || '').toUpperCase().slice(0, 16);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    class_pref: '12',
    track_pref: 'JEE',
    referral_code: prefillRef,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (prefillRef) setForm((f) => ({ ...f, referral_code: prefillRef }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillRef]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        class_pref: Number(form.class_pref),
        track_pref: form.track_pref,
      };
      if (form.referral_code && form.referral_code.trim()) {
        payload.referral_code = form.referral_code.trim().toUpperCase();
      }
      await register(payload);
      toast.success('Account created — let’s begin!');
      nav('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-black text-white">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0"
        style={{
          backgroundImage:
            'radial-gradient(900px circle at 90% 10%, rgba(220,38,38,0.28), transparent 55%), radial-gradient(700px circle at 10% 30%, rgba(239,68,68,0.16), transparent 60%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
        }}
      />

      <div className="relative z-10">
        <header className="border-b border-white/10">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-red-600 text-white shadow-[0_0_28px_rgba(239,68,68,0.55)]">
                <GraduationCap className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-base font-semibold">StudyTrack AI</div>
                <div className="text-[11px] text-white/50">NCERT · JEE · NEET</div>
              </div>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </div>
        </header>

        <div className="mx-auto flex min-h-[calc(100vh-64px)] max-w-6xl items-center justify-center px-4 py-10 sm:px-6">
          <div className="grid w-full max-w-5xl gap-10 md:grid-cols-2">
            <div className="hidden flex-col justify-center gap-5 md:flex">
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-300">
                <Flame className="h-3.5 w-3.5" />
                Level up. In 60 seconds.
              </div>
              <h1 className="font-display text-4xl font-bold leading-tight sm:text-5xl">
                Own your{' '}
                <span className="bg-gradient-to-b from-red-400 to-red-600 bg-clip-text text-transparent">
                  weak topics.
                </span>
              </h1>
              <p className="max-w-md text-white/70">
                Save progress · track weak areas · reattempt smarter. Free forever to start.
              </p>
            </div>

            <div className="w-full rounded-2xl border border-white/10 bg-black/60 p-6 shadow-2xl backdrop-blur sm:p-8">
              <div className="mb-6">
                <div className="font-display text-2xl font-semibold">Create your account</div>
                <div className="mt-1 text-sm text-white/60">No credit card. No spam.</div>
              </div>
              <form className="space-y-4" onSubmit={onSubmit}>
                <GoogleLoginButton label="Sign up with Google" testId="register-google-button" />
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-white/15" />
                  <span className="text-[11px] uppercase tracking-widest text-white/40">or sign up with email</span>
                  <div className="h-px flex-1 bg-white/15" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-white/80">Full name</Label>
                  <Input
                    id="name"
                    required
                    value={form.name}
                    onChange={onChange('name')}
                    data-testid="register-name-input"
                    className="h-11 border-white/15 bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:ring-red-500"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-white/80">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange('email')}
                    data-testid="register-email-input"
                    className="h-11 border-white/15 bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:ring-red-500"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-white/80">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={6}
                    value={form.password}
                    onChange={onChange('password')}
                    data-testid="register-password-input"
                    className="h-11 border-white/15 bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:ring-red-500"
                  />
                  <div className="text-xs text-white/40">Min. 6 characters.</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-white/80">Class</Label>
                    <Select value={form.class_pref} onValueChange={(v) => setForm({ ...form, class_pref: v })}>
                      <SelectTrigger data-testid="register-class-select" className="h-11 border-white/15 bg-white/[0.04] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="11">Class 11</SelectItem>
                        <SelectItem value="12">Class 12</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-white/80">Target</Label>
                    <Select value={form.track_pref} onValueChange={(v) => setForm({ ...form, track_pref: v })}>
                      <SelectTrigger data-testid="register-track-select" className="h-11 border-white/15 bg-white/[0.04] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BOARDS_THEORY">Boards · Theory</SelectItem>
                        <SelectItem value="BOARDS_PRACTICAL">Boards · Practicals</SelectItem>
                        <SelectItem value="JEE">JEE</SelectItem>
                        <SelectItem value="NEET">NEET</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5 text-white/80">
                    <Gift className="h-3.5 w-3.5 text-red-400" />
                    Referral code
                    <span className="text-white/40">(optional)</span>
                  </Label>
                  <Input
                    value={form.referral_code}
                    onChange={(e) =>
                      setForm({ ...form, referral_code: (e.target.value || '').toUpperCase().slice(0, 16) })
                    }
                    placeholder={prefillRef ? prefillRef : 'e.g. ANKITAB4'}
                    data-testid="register-referral-input"
                    className="h-11 border-white/15 bg-white/[0.04] font-mono-ui uppercase tracking-wider text-white placeholder:text-white/40 focus-visible:ring-red-500"
                  />
                  {prefillRef && (
                    <div className="text-xs text-red-300">
                      Applied invite code — you and your friend both earn a bonus chapter when you upgrade.
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="h-11 w-full gap-2 bg-red-600 font-semibold text-white shadow-[0_0_28px_rgba(239,68,68,0.45)] hover:bg-red-500"
                  data-testid="register-submit-button"
                >
                  <Rocket className="h-4 w-4" />
                  {loading ? 'Creating…' : 'Create account'}
                </Button>
                <p className="text-sm text-white/60">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="text-red-400 underline-offset-2 hover:underline"
                    data-testid="go-to-login-link"
                  >
                    Log in
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
